import { buildTutorialTargetMissingHint } from '../buildTargetMissingHint';
import type { TutorialDefinition, TutorialRecover } from '../types';

const R = {
  page: { path: '/todo-note', pageLabel: 'ToDo 노트' },
  category: {
    path: '/todo-note',
    openSelector: '.cp-todo-note-chip-add, [data-tutorial-todo="chip-add"]',
    pageLabel: 'ToDo 노트',
    openLabel: '카테고리 추가',
  },
  memo: {
    path: '/todo-note',
    openSelector: '.cp-todo-note-fab-add, [data-tutorial-todo="fab-add"]',
    pageLabel: 'ToDo 노트',
    openLabel: '메모 추가',
  },
  routine: {
    path: '/todo-note',
    openSelector: '.cp-todo-note-fab-routine, [data-tutorial-todo="fab-routine"]',
    pageLabel: 'ToDo 노트',
    openLabel: '루틴 추가',
  },
  noteSettings: {
    path: '/todo-note',
    openSelector: '[data-tutorial-todo="open-note-settings"]',
    pageLabel: 'ToDo 노트',
    openLabel: '노트 설정',
  },
} as const satisfies Record<string, TutorialRecover>;

const RECOVER_BY_STEP_ID: Record<string, TutorialRecover> = {
  'go-todo-note': R.page,
  'view-toggle': R.page,
  'open-category': R.page,
  'category-modal': R.category,
  'open-memo': R.page,
  'memo-fields': R.memo,
  'save-memo': R.memo,
  'complete-todo': R.page,
  'delete-guide': R.page,
  'dup-guide': R.page,
  'open-routine': R.page,
  'routine-settings': R.routine,
  'save-routine': R.routine,
  'save-all': R.page,
  'open-note-settings': R.page,
  'toggle-share': R.noteSettings,
  'share-guide': R.noteSettings,
};

const MODAL_CLOSE_LOCK = '.cp-btn-secondary, [aria-label="닫기"]';

const todoNoteRaw: TutorialDefinition = {
  id: 'todo-note',
  title: 'Todo 노트',
  summary: '카테고리·메모·루틴·공유 설정까지 Todo 노트 사용법을 배워요.',
  steps: [
    {
      id: 'intro',
      mascot:
        'Todo 노트 튜토리얼을 시작해요! 카테고리, 메모, 루틴, 공유 설정까지 순서대로 알려드릴게요.',
      actionLabel: '시작하기',
      presentation: 'intro',
      cardTitle: 'Todo 노트를 시작해요!',
      placement: 'center',
    },
    {
      id: 'go-todo-note',
      mascot: '사이드바에서 "ToDo 노트"를 눌러 이동해 주세요.',
      targetSelector: 'a[href^="/todo-note"]',
      placement: 'right',
      section: '[Todo 노트] 시작',
      sectionMascot: 'Todo 노트 페이지로 이동하는 구간이에요.',
    },
    {
      id: 'view-toggle',
      mascot:
        '오른쪽 위 "타임테이블"·"캘린더"·"포스트잇"을 눌러 모드가 바뀌는 걸 확인해 보세요. 이어서 진행하려면 다시 "타임테이블"로 맞춘 뒤 "다음"을 눌러 주세요.',
      targetSelector: '.cp-todo-note-view-toggle, [data-tutorial-todo="view-toggle"]',
      manualAdvance: true,
      manualAdvanceEnableWhen:
        '[data-tutorial-todo="view-timetable"].is-active, [data-tutorial-todo="view-timetable"][aria-selected="true"]',
      section: '[Todo 노트] 화면 둘러보기',
      sectionMascot: '화면 보기 방식과 도구를 알아보는 구간이에요.',
    },
    {
      id: 'open-category',
      mascot:
        '카테고리 칩의 "+"를 눌러 새 카테고리를 만들어 보세요. 이미 충분하면 "다음"으로 건너뛰어도 돼요.',
      targetSelector: '.cp-todo-note-chip-add, [data-tutorial-todo="chip-add"]',
      advanceWhenSelectorExists: '.cp-todo-note-category-modal',
      manualAdvance: true,
      manualAdvanceSkipToStepId: 'open-memo',
      section: '[Todo 노트] 카테고리',
      sectionMascot: '할 일을 분류할 카테고리를 만드는 구간이에요.',
    },
    {
      id: 'category-modal',
      mascot:
        '이름과 색을 정한 뒤 "추가"를 눌러 주세요. 만들지 않을 거면 창을 닫아도 다음으로 넘어가요.',
      targetSelector: '.cp-todo-note-category-modal',
      advanceSelector: '.cp-todo-note-category-modal .cp-btn-primary',
      advanceWhenSelectorGone: '.cp-todo-note-category-modal',
    },
    {
      id: 'open-memo',
      mascot: '오른쪽 아래 "+" 버튼을 눌러 메모(TODO)를 추가해 주세요.',
      targetSelector: '.cp-todo-note-fab-add, [data-tutorial-todo="fab-add"]',
      advanceWhenSelectorExists: '.cp-todo-note-item-add-overlay',
      section: '[Todo 노트] 메모 추가',
      sectionMascot: '할 일 메모를 작성하고 완료 표시까지 해보는 구간이에요.',
    },
    {
      id: 'memo-fields',
      mascot:
        '마감일·카테고리·메모를 채워 보세요. 첫 줄이 제목으로 쓰여요. 다듬은 뒤 "다음"을 눌러 주세요.',
      targetSelector: '.cp-todo-note-item-add-overlay .cp-todo-note-editor-blocks',
      manualAdvance: true,
    },
    {
      id: 'save-memo',
      mascot: '"확인"을 눌러 메모를 저장해 주세요.',
      targetSelector: '.cp-todo-note-item-add-overlay',
      advanceSelector:
        '.cp-todo-note-item-add-actions .cp-btn-primary:not([disabled])',
      blockClickSelectors: MODAL_CLOSE_LOCK,
      targetMissingHint:
        '메모 창이 열려 있는지 확인해 주세요. 마감일이 있어야 확인 버튼이 켜져요.',
    },
    {
      id: 'complete-todo',
      mascot:
        '목록에서 완료 동그라미를 눌러 완료 처리를 해 보세요. 바꾼 뒤 "다음"을 눌러 주세요.',
      targetSelector: '.cp-todo-note-complete, [data-tutorial-todo="row-complete"]',
      manualAdvance: true,
      targetMissingHint:
        '완료할 메모 행이 아직 없어요. 앞에서 메모를 저장한 뒤 다시 진행하거나 "다음"으로 넘어가도 돼요.',
    },
    {
      id: 'delete-guide',
      mascot:
        '메모 행의 휴지통은 짧게 누르면 안 지워지고, 길게 눌러야 삭제돼요. 지금은 살펴만 보고 "다음"을 눌러 주세요.',
      targetSelector: '.cp-todo-note-remove-btn, [data-tutorial-todo="row-delete"]',
      manualAdvance: true,
      blockTargetClick: true,
      section: '[Todo 노트] 삭제·복제',
      sectionMascot: '삭제·복제 버튼 사용법을 알아보는 구간이에요.',
      targetMissingHint:
        '삭제 버튼은 메모 행에 있어요. 목록이 비어 있으면 "다음"으로 넘어가도 돼요.',
    },
    {
      id: 'dup-guide',
      mascot:
        '같은 줄의 복제 버튼으로 블럭을 바로 아래에 복사할 수 있어요. 지금은 살펴만 보고 "다음"을 눌러 주세요.',
      targetSelector: '.cp-todo-note-dup-btn, [data-tutorial-todo="row-dup"]',
      manualAdvance: true,
      blockTargetClick: true,
      targetMissingHint:
        '복제 버튼은 일반 메모 행에 있어요(루틴 발생 행에는 없을 수 있어요). 없으면 "다음"으로 넘어가도 돼요.',
    },
    {
      id: 'open-routine',
      mascot: '반복되는 일은 "루틴"으로 등록해요. 오른쪽 아래 루틴 버튼을 눌러 주세요.',
      targetSelector: '.cp-todo-note-fab-routine, [data-tutorial-todo="fab-routine"]',
      advanceWhenSelectorExists: '.cp-todo-routine-create-stack',
      section: '[Todo 노트] 루틴',
      sectionMascot: '반복 일정을 루틴으로 만드는 구간이에요.',
    },
    {
      id: 'routine-settings',
      mascot:
        '매일·매주·매월과 요일·기간을 정해 보세요. 설정한 뒤 "다음"을 눌러 주세요.',
      targetSelector: '.cp-todo-routine-settings',
      manualAdvance: true,
    },
    {
      id: 'save-routine',
      mascot: '메모를 적은 뒤 "추가"를 눌러 루틴을 저장해 주세요.',
      targetSelector: '.cp-todo-routine-create-stack',
      advanceSelector:
        '.cp-todo-routine-create-stack .cp-todo-note-item-add-actions .cp-btn-primary:not([disabled])',
      blockClickSelectors: MODAL_CLOSE_LOCK,
    },
    {
      id: 'save-all',
      mascot: '변경한 내용을 "저장하기"로 저장해 주세요.',
      targetSelector: '.cp-btn-save-all:not([disabled]), .cp-fab-save:not([disabled])',
      manualAdvance: true,
      section: '[Todo 노트] 저장',
      sectionMascot: '작업한 내용을 저장하는 구간이에요.',
      targetMissingHint:
        '저장할 변경이 없으면 버튼이 비활성일 수 있어요. 이미 반영했다면 "다음"으로 넘어가도 돼요.',
    },
    {
      id: 'open-note-settings',
      mascot: '선택한 노트 이름 옆의 연필(노트 설정)을 눌러 주세요.',
      targetSelector: '[data-tutorial-todo="open-note-settings"]',
      advanceWhenSelectorExists:
        '[data-tutorial-todo="note-settings-modal"], .cp-todo-share-modal',
      section: '[Todo 노트] 공유',
      sectionMascot: '노트를 공유해 함께 작업하는 방법을 알아보는 구간이에요.',
    },
    {
      id: 'toggle-share',
      mascot:
        '상단에서 "공유"를 눌러 비공개를 공유로 바꿔 주세요. 이미 공유 중이면 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-todo="share-toggle"], .cp-todo-share-active-toggle',
      manualAdvanceIfExists:
        '[data-tutorial-todo="share-toggle"].is-on, .cp-todo-share-active-toggle.is-on',
    },
    {
      id: 'share-guide',
      mascot:
        '입장코드와 공유 링크로 다른 사람과 같은 Todo 노트를 함께 작업할 수 있어요. 링크는 "링크 복사"로 보낼 수 있어요. 확인했으면 "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-todo="share-code"], [data-tutorial-todo="share-link"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'outro',
      mascot:
        'Todo 노트 튜토리얼을 마쳤어요! 필요할 때 계정 메뉴의 "튜토리얼"에서 다시 볼 수 있어요.',
      actionLabel: '완료',
      presentation: 'celebration',
      cardTitle: '잘하셨어요!',
      placement: 'center',
    },
  ],
};

const todoNote: TutorialDefinition = {
  ...todoNoteRaw,
  steps: todoNoteRaw.steps.map((step) => {
    const recover = RECOVER_BY_STEP_ID[step.id];
    if (!recover && !step.targetMissingHint) return step;
    return {
      ...step,
      ...(recover ? { recover } : {}),
      targetMissingHint: buildTutorialTargetMissingHint(recover, step.targetMissingHint),
    };
  }),
};

export default todoNote;
